import { DailyStock } from "@/api/daily-stock-api";
import { useBulkReceiveStock } from "@/hooks/product/use-bulk-receive-stock";
import { useDebounce } from "@/hooks/use-debounce";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import {
  stockService,
  useRecomputeAllStock,
  useTodayDailyStock,
} from "@/services/daily-stock-service";
import { useState } from "react";

export function useStockPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("out-of-stock");
  const [showBulkReceiveModal, setShowBulkReceiveModal] = useState(false);
  const isMobile = useIsMobile();

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Map tab to server-side status param
  const statusParam =
    activeTab === "out-of-stock"
      ? "out-of-stock"
      : activeTab === "low-stock"
        ? "low-stock"
        : undefined;

  const {
    data: stocks,
    totalElements,
    isLastPage,
    isLoading,
    isValidating,
    error,
    loadMore,
    refresh: refetchStock,
  } = useTodayDailyStock({
    search: debouncedSearchTerm || undefined,
    status: statusParam,
  });

  const { executeAsync: recomputeAllStock } = useRecomputeAllStock();

  const { handleBulkReceiveStock } = useBulkReceiveStock();

  const [adjustOutDialogState, setAdjustOutDialogState] = useState<{
    isOpen: boolean;
    selectedStock: DailyStock | null;
  }>({
    isOpen: false,
    selectedStock: null,
  });

  const [adjustInDialogState, setAdjustInDialogState] = useState<{
    isOpen: boolean;
    selectedStock: DailyStock | null;
  }>({
    isOpen: false,
    selectedStock: null,
  });

  // Client-side stats computed from all loaded pages
  const stats = stockService.getStockStats(stocks);

  // filteredStocks == stocks since filtering is now server-side
  const filteredStocks = Array.isArray(stocks) ? stocks : [];

  const handleRecomputeAllStock = async () => {
    try {
      const res = await recomputeAllStock(undefined);
      if (!res.success) {
        toast({
          title: "Recompute stock error",
          description:
            res.message || "Error occurred during recomputing stocks...",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Stock recompute job started successfully.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Recompute stock error",
        description:
          error?.message || "Error occurred during recomputing stocks...",
        variant: "destructive",
      });
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    showBulkReceiveModal,
    setShowBulkReceiveModal,
    stocks,
    filteredStocks,
    stats,
    totalElements,
    isLastPage,
    isLoading,
    isValidating,
    error,
    isMobile,
    handleRecomputeAllStock,
    handleBulkReceiveStock,
    adjustOutDialogState,
    setAdjustOutDialogState,
    adjustInDialogState,
    setAdjustInDialogState,
    refetchStock,
    loadMore,
  };
}
